import React, {useMemo} from 'react';
import {Button} from 'antd';
import {useTranslation} from 'react-i18next';
import {SorterFiltersType} from "../hooks/useSorterQuery";
import InputFilterItem from "../../../delivery/components/InputFilterItem";
import ParcelFilterStyle from "../../../delivery/components/ParcelFilterStyle";
import {getSorterStatusOptions, getSorterTypeOptions} from "../config/sorterFilterOptions";
import {getSmartLabelClassName} from "../../../../utils/labelWidthUtils";

interface SorterFilterProps {
    filters: SorterFiltersType;
    setFilters: (filters: SorterFiltersType) => void;
    handleFilter: () => void;
    handleResetFilters: () => void;
    onAddSorter: () => void;
}

const SorterFilter: React.FC<SorterFilterProps> = ({
    filters,
    setFilters,
    handleFilter,
    handleResetFilters,
    onAddSorter,
}) => {
    const {t} = useTranslation();

    // 使用国际化的选项
    const sorterStatusOptions = useMemo(() => getSorterStatusOptions(t), [t]);
    const sorterTypeOptions = useMemo(() => getSorterTypeOptions(t), [t]);

    // Label constants for alignment
    const labelSorterCode = String(t('wms.sorter.filters.sorterCode'));
    const labelSorterName = String(t('wms.sorter.filters.sorterName'));
    const labelStatus = String(t('wms.sorter.filters.status'));
    const labelType = String(t('wms.sorter.filters.type'));

    // Calculate label width for column alignment (一行三个筛选项)
    const col1Labels = [labelSorterCode, labelType];
    const col2Labels = [labelSorterName];
    const col3Labels = [labelStatus];

    const col1SpanClassName = getSmartLabelClassName(col1Labels);
    const col2SpanClassName = getSmartLabelClassName(col2Labels);
    const col3SpanClassName = getSmartLabelClassName(col3Labels);

    // 处理分拣机编码变化
    const handleSorterCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilters({...filters, sorterCode: e.target.value || undefined});
    };

    // 处理分拣机名称变化
    const handleSorterNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilters({...filters, sorterName: e.target.value || undefined});
    };

    // 处理分拣机状态变化
    const handleStatusChange = (value: string) => {
        setFilters({...filters, sorterStatus: value ? parseInt(value) : undefined});
    };

    // 处理分拣机类型变化
    const handleTypeChange = (value: string) => {
        setFilters({...filters, sorterType: value ? parseInt(value) : undefined});
    };

    return (
        <div className="mb-2 pl-0 py-2">
            {/* 使用 Grid 布局确保列对齐 */}
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                {/* 分拣机编码筛选 */}
                <div className="min-w-0">
                    <InputFilterItem
                        label={labelSorterCode}
                        value={filters.sorterCode?.toString() || ''}
                        onChange={handleSorterCodeChange}
                        spanClassName={col1SpanClassName}
                    />
                </div>

                {/* 分拣机名称筛选 */}
                <div className="min-w-0">
                    <InputFilterItem
                        label={labelSorterName}
                        value={filters.sorterName?.toString() || ''}
                        onChange={handleSorterNameChange}
                        spanClassName={col2SpanClassName}
                    />
                </div>

                {/* 分拣机状态筛选 */}
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelStatus}
                        value={filters.sorterStatus !== undefined ? filters.sorterStatus.toString() : ''}
                        onChange={handleStatusChange}
                        options={sorterStatusOptions}
                        spanClassName={col3SpanClassName}
                    />
                </div>

                {/* 分拣机类型筛选 */}
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelType}
                        value={filters.sorterType !== undefined ? filters.sorterType.toString() : ''}
                        onChange={handleTypeChange}
                        options={sorterTypeOptions}
                        spanClassName={col1SpanClassName}
                    />
                </div>
            </div>

            {/* 操作按钮 */}
            <div className="flex justify-end gap-3 mt-4">
                <Button
                    type="primary"
                    onClick={onAddSorter}
                    className="font-semibold min-w-[100px]"
                >
                    {t('wms.sorter.buttons.addSorter')}
                </Button>
                <Button type="primary" onClick={handleFilter} className="font-semibold min-w-[80px]">
                    {t('wms.sorter.buttons.filter')}
                </Button>
                <Button onClick={handleResetFilters} className="font-semibold min-w-[80px]">
                    {t('wms.sorter.buttons.reset')}
                </Button>
            </div>
        </div>
    );
};

export default SorterFilter;

